import { get } from 'lodash-es'
import { getErrorMessage, OPERA_URL } from '@/main'
import Axios from 'axios'
import { prepareChemicals, getChemicalUniqueKey } from '@/utils/chemical'
import {
  constructErrorResultsForEachChemical,
  enrichResponseWithSentStructures,
  defaultChemicalKey,
  defaultResultsKey,
  constructValidationErrorResultsForEachChemical,
  partitionChemicalsByValidation,
} from '@/utils/responseNormalizers'
import { modelStateRegistry } from '@/utils/modelStateRegistry'

const operaState = get(modelStateRegistry, 'opera')

const state = {
  ...operaState.createState(),
}

const getters = {
  ...operaState.createGetters(),
}

const mutations = {
  ...operaState.createMutations(),
}

const keys = operaState.keys

const getProgressPercentage = ({ completedCount, totalCount }) => {
  if (!totalCount) return null
  return (completedCount / totalCount) * 100
}

const getCurrentResults = (getters) => getters[keys.getResults]

const actions = {
  async predict(context, payload) {
    const { getters, commit } = context
    commit(keys.RESET_STATE)

    const structures = payload?.structures

    const { validChemicals, invalidChemicalsWithError } = partitionChemicalsByValidation(structures)

    const validationErrorResults = constructValidationErrorResultsForEachChemical({
      invalidChemicalsWithError,
      resultsKey: defaultResultsKey,
      chemicalKey: defaultChemicalKey,
    })

    commit(keys.PUSH_RESULTS, validationErrorResults)

    if (!validChemicals?.length) {
      return getCurrentResults(getters)
    }

    commit(keys.SET_PROGRESS, 0)
    commit(keys.SET_LOADING, true)

    const abortController = new AbortController()
    commit(keys.SET_ABORT_CONTROLLER, abortController)

    const handlePredictError = (error, sentStructures) => {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return getCurrentResults(getters)
      }

      console.error('Opera predict error:', error)

      const networkErrorResults = constructErrorResultsForEachChemical({
        sentStructures,
        errorMessage: getErrorMessage(error),
        resultsKey: defaultResultsKey,
        chemicalKey: defaultChemicalKey,
      })

      commit(keys.PUSH_RESULTS, networkErrorResults)
      return getCurrentResults(getters)
    }

    try {
      const firstResponse = await this._vm.$axios.post(
        OPERA_URL,
        {
          chemicals: prepareChemicals(validChemicals, getChemicalUniqueKey),
          cache_only: true,
        },
        { signal: abortController.signal },
      )

      const firstResponseData = enrichResponseWithSentStructures({
        responseData: firstResponse.data,
        sentStructures: validChemicals,
        resultsKey: defaultResultsKey,
        chemicalKey: defaultChemicalKey,
        getSentChemicalIdentifier: getChemicalUniqueKey,
        getReceivedChemicalIdentifier: (chemical) => chemical?.id,
      })

      const missingChemicals = firstResponse.data?.missing_chemicals || []

      commit(keys.PUSH_RESULTS, firstResponseData)

      commit(
        keys.SET_PROGRESS,
        getProgressPercentage({
          completedCount: validChemicals.length - missingChemicals.length,
          totalCount: validChemicals.length,
        }),
      )

      if (!missingChemicals.length) {
        commit(keys.SET_PROGRESS, 100)
        return getCurrentResults(getters)
      }

      commit(keys.SET_PROGRESS_LABEL, 'Calculating results may take more than one minute')

      return await this._vm.$axios
        .post(OPERA_URL, { chemicals: missingChemicals }, { signal: abortController.signal })
        .then((secondResponse) => {
          const secondResponseData = enrichResponseWithSentStructures({
            responseData: secondResponse.data,
            sentStructures: validChemicals,
            resultsKey: defaultResultsKey,
            chemicalKey: defaultChemicalKey,
            getSentChemicalIdentifier: getChemicalUniqueKey,
            getReceivedChemicalIdentifier: (chemical) => chemical?.id,
          })

          commit(keys.PUSH_RESULTS, secondResponseData)

          commit(keys.SET_PROGRESS, 100)
          return getCurrentResults(getters)
        })
        .catch((error) => handlePredictError(error, missingChemicals))
    } catch (error) {
      handlePredictError(error, validChemicals)
    } finally {
      commit(keys.SET_PROGRESS, null)
      commit(keys.SET_PROGRESS_LABEL, null)
      commit(keys.SET_LOADING, false)
      commit(keys.SET_ABORT_CONTROLLER, null)
    }
  },
}

const prepareOperaMetadata = (context) => {
  const { commit } = context
  return Axios.get(`${OPERA_URL}/metadata`)
    .then((response) => {
      commit(`opera/${keys.SET_METADATA}`, response.data)
      commit('setFeatures', { opera: { category: 'service' } })
    })
    .catch((error) => {
      console.warn('OPERA metadata is not available: %o', getErrorMessage(error))
      commit('removeFeature', { opera: {} })
    })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}

export { prepareOperaMetadata }
